import { ChangeEvent } from 'react';
import { Box, Typography, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';

const SMS_STANDARD_MAX = 160;

interface SmsPhoneMockupProps {
  message: string;
  enrichi: boolean;
  onMessageChange: (value: string) => void;
  onEnrichiChange: (value: boolean) => void;
}

function SmsPhoneMockup({ message, enrichi, onMessageChange, onEnrichiChange }: SmsPhoneMockupProps) {
  const { sidebarColor } = useLayoutPreferences();
  const charCount = message.length;
  const smsCount = Math.max(1, Math.ceil(charCount / SMS_STANDARD_MAX));

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box
        sx={{
          width: 290,
          border: '16px solid #1a1a1a',
          borderRadius: '40px',
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3), 0 0 0 2px #333',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ backgroundColor: '#1a1a1a', display: 'flex', justifyContent: 'center', alignItems: 'center', py: 0.75, gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#444' }} />
          <Box sx={{ width: 32, height: 6, borderRadius: 3, backgroundColor: '#444' }} />
        </Box>

        <Box sx={{ backgroundColor: '#fff', display: 'flex', flexDirection: 'column', minHeight: 460 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              py: 1,
              px: 1.5,
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
            }}
          >
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.78rem', color: '#495057' }}>
              {smsCount} sms - {charCount} caractère(s)
            </Typography>
            <Tooltip title={`1 SMS = ${SMS_STANDARD_MAX} caractères. Au-delà, le message est découpé en plusieurs SMS.`} arrow>
              <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: '#adb5bd', cursor: 'help' }} />
            </Tooltip>
          </Box>

          <Box
            component="textarea"
            value={message}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onMessageChange(e.target.value)}
            placeholder="Ecrivez votre message ici..."
            sx={{
              flexGrow: 1,
              minHeight: 360,
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              p: 1.5,
              fontSize: '0.83rem',
              fontFamily: 'inherit',
              color: '#212529',
              backgroundColor: 'transparent',
              lineHeight: 1.6,
              boxSizing: 'border-box',
              '&::placeholder': { color: '#adb5bd' },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 0.75,
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={enrichi}
                  onChange={e => onEnrichiChange(e.target.checked)}
                  sx={{ py: 0.25, '&.Mui-checked': { color: sidebarColor } }}
                />
              }
              label={
                <Typography variant="caption" sx={{ fontSize: '0.73rem', color: '#495057' }}>
                  Ce message est un <strong>SMS enrichi</strong>
                </Typography>
              }
              sx={{ mx: 0 }}
            />
          </Box>
        </Box>

        <Box sx={{ backgroundColor: '#1a1a1a', display: 'flex', justifyContent: 'center', py: 1 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: '2px solid #444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 12, height: 12, border: '2px solid #444', borderRadius: 1.5 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SmsPhoneMockup;
